import React, {useMemo, useRef} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../base/form";
import {OutlinedDropdownForm} from "../../../../base/dropdown/outlined_dropdown";
import Button from "../../../../base/button/button";
import useCountryProvinceCache from "../../../../../hooks/use-country-province-cache";
import {Col} from "reactstrap";
import EnvService from "../../../../../../core/services/env";


const formNames = {
    country: 'addProvince_country',
    province: 'addProvince_province',
}

const schema = Yup.object().shape({
    [formNames.country]: Yup.object().nullable().required(ValidateMessages.required),
    [formNames.province]: Yup.object().nullable().required(ValidateMessages.required),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);


const UpsertFreightRegionDialogAddProvinceSection = ({addProvince, selectedProvinces}) => {
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const {countries, onCountrySelected} = useCountryProvinceCache();

    const initialValues = useMemo(() => ({
        [formNames.country]: null,
        [formNames.province]: null,
    }), [])

    /**
     * Submits the form by calling the appropriate callback with the country value and then removes the value from the fields.
     * @param values
     * @return {Promise<void>}
     */
    const onSubmit = async (values) => {
        addProvince(values[formNames.province]);
        setTimeout(() => {
            formRef.current?.change(formNames.province, initialValues[formNames.province]);
            formRef.current?.resetFieldState(formNames.province);
        }, 300);
    }

    /**
     * Changes the value of province and country and if the provinces of that country is not yet loaded, gets them
     * from the server.
     *
     * @param e
     * @param form {any}
     */
    const onCountryChanged = (e, form) => {
        form.change(e.target.name, e.target.value);
        form.change(formNames.province, initialValues[formNames.province]);
        form.resetFieldState(formNames.province);
        const countryId = e.target.value?.id;
        onCountrySelected(countryId);
    }

    return (
        <>
            <Form
                className={'d-flex flex-wrap w-100'}
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                render={({form, values}) => {
                    formRef.current = form;
                    const provinces = !!values?.[formNames.country]
                        ? countries?.find(e => e.id === values[formNames.country]?.id)?.provinces ?? []
                        : [];
                    const selectedIds = selectedProvinces.map(e => e.id);
                    const visibleProvinces = provinces.filter(e => !selectedIds.includes(e.id));

                    return (
                        <div className={'w-100 d-flex flex-wrap'}>
                            <Col xs={12} md={5} className={'mb-3'}>
                                <OutlinedDropdownForm
                                    label={'Country'}
                                    name={formNames.country}
                                    required={!!required[formNames.country]}
                                    data={countries?.map(e => ({
                                        value: e,
                                        label: e.name,
                                        id: e.id,
                                    })) ?? []}
                                    onChange={(e) => onCountryChanged(e, form)}
                                />
                            </Col>
                            <Col xs={12} md={5} className={'mb-3'}>
                                <OutlinedDropdownForm
                                    label={!values[formNames.country] ? 'Province / State' : values[formNames.country]?.name === EnvService.USATitle ? "State" : 'Province'}
                                    name={formNames.province}
                                    required={!!required[formNames.province]}
                                    renderValue={v => v?.name ?? v?.code}
                                    data={visibleProvinces?.map(e => ({
                                        value: e,
                                        label: e.name ?? e.code,
                                        id: e.id,
                                    })) ?? []}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <Button
                                    variant={'contained'}
                                    color={'primary'}
                                    type={'button'}
                                    className={'px-5 px-md-4 px-lg-5 py-2-5 mx-auto d-flex'}
                                    onClick={() => formRef.current.submit()}
                                >
                                    Add
                                </Button>
                            </Col>
                        </div>
                    );
                }}
            />
        </>
    );
}

export default UpsertFreightRegionDialogAddProvinceSection;
